import React, {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useWorkspace } from 'wrappers/WorkspaceProvider';

import type { Permission } from '@fragment/authorization-utils';
import { rolePermissions } from '@fragment/authorization-utils/rolePermissions';

type WorkspacePermissionContextValue = {
  hasPermission: (permission: Permission) => boolean;
};

const defaultContext: WorkspacePermissionContextValue = {
  hasPermission: () => false,
};

const WorkspacePermissionContext =
  createContext<WorkspacePermissionContextValue>(defaultContext);

export const WorkspacePermissionProvider = ({
  children,
}: PropsWithChildren) => {
  const { workspace } = useWorkspace();

  const permissions = useMemo(
    () => (workspace ? rolePermissions[workspace.role] : null),
    [workspace]
  );

  const hasPermission = useCallback(
    (permission: Permission) => permissions?.has(permission) ?? false,
    [permissions]
  );

  const value = useMemo(
    () => ({
      hasPermission,
    }),
    [hasPermission]
  );

  return (
    <WorkspacePermissionContext.Provider value={value}>
      {children}
    </WorkspacePermissionContext.Provider>
  );
};

export const useWorkspacePermissionContext = () =>
  useContext(WorkspacePermissionContext);
